import React, { useEffect, useState } from "react";
import i18next from "i18next";
import {
  optionsCallToActionAppPromotionTikTokBusiness,
  optionsCallToActionAwarenessTikTokBusiness,
  optionsCallToActionEngagementTikTokBusiness,
  optionsCallToActionLeadsTikTokBusiness,
  optionsCallToActionSalesTikTokBusiness,
  optionsCallToActionTrafficTikTokBusiness,
} from "../../../../utils/options";
import {
  ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_TRAFFIC_OPTION,
} from "../../../../utils/constants";
import { FIELD_REQUIRED } from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import Select from "../../../Form/Select";
import { useController, useFormContext } from "react-hook-form";
import SelectMultiple from "../../../Form/SelectMultiple";
import _ from "lodash";

const dynamicCallToAction = "Dynamic";

const SelectCallToActionTikTok = React.forwardRef(
  function SelectCallToActionTikTok(props, ref) {
    const {
      control,
      errors,
      rules = {
        required: {
          value: true,
          message: i18next.t(FIELD_REQUIRED),
        },
      },
      mode = "single",
      nameSingle,
      nameMultiple,
      label,
      disabled,
      platform,
      destinationType = ADS_CAMPAIGN_META_DESTINATION_TYPE_WEBSITE_OPTION,
      fullWidth,
      variant = "filled",
      objective,
      sx = {},
    } = props;

    const name = mode === "multiple" ? nameMultiple : nameSingle;

    const [options, setOptions] = useState([]);
    const { t } = useTranslationApp();

    const { setValue } = useFormContext();
    const {
      field: { value: fieldValue },
    } = useController({
      control,
      name,
    });

    const isDynamicIncompatible =
      mode === "multiple" &&
      fieldValue?.length === 1 &&
      fieldValue?.includes(dynamicCallToAction);

    useEffect(() => {
      let opts = [];
      if (mode === "single") {
        if (!objective) return;
        if (objective === ADS_CAMPAIGN_OBJECTIVE_AWARENESS_OPTION)
          opts = optionsCallToActionAwarenessTikTokBusiness;
        if (objective === ADS_CAMPAIGN_OBJECTIVE_TRAFFIC_OPTION)
          opts = optionsCallToActionTrafficTikTokBusiness;
        if (objective === ADS_CAMPAIGN_OBJECTIVE_ENGAGEMENT_OPTION)
          opts = optionsCallToActionEngagementTikTokBusiness;
        if (objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION)
          opts = optionsCallToActionLeadsTikTokBusiness;
        if (objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION)
          opts = optionsCallToActionAppPromotionTikTokBusiness;
        if (objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION)
          opts = optionsCallToActionSalesTikTokBusiness;
      } else if (mode === "multiple") {
        if (Array.isArray(fieldValue) && fieldValue?.length > 0) {
          opts = fieldValue.map((v) => ({ value: v, label: v }));
        }
      }
      setOptions(opts);
      // eslint-disable-next-line
    }, [objective, platform, destinationType]);

    useEffect(() => {
      if (options?.length === 0) return;
      if (_.isEmpty(fieldValue)) {
        if (mode === "single") {
          setValue(name, options?.[0].value, { shouldDirty: false });
        } else {
          setValue(name, [options?.[0].value], { shouldDirty: false });
        }
        return;
      }

      if (mode === "single") {
        const isValidCallToAction = options.some((o) => o.value === fieldValue);
        if (!isValidCallToAction) {
          if (mode === "single") {
            setValue(name, options?.[0].value, { shouldDirty: false });
          } else {
            setValue(name, [options?.[0].value], { shouldDirty: false });
          }
        }
      }
      // eslint-disable-next-line
    }, [options]);

    const isDisabled =
      disabled ||
      isDynamicIncompatible ||
      (mode === "multiple" &&
        (!Array.isArray(fieldValue) || fieldValue?.length === 0));

    if (mode === "multiple") {
      return (
        <SelectMultiple
          ref={ref}
          label={t(label)}
          disabled={isDisabled}
          name={name}
          variant={variant}
          options={options}
          fullWidth={fullWidth}
          doTranslate={false}
          control={control}
          errors={errors}
          rules={{
            ...rules,
            validate: (value) => {
              if (!options.map((o) => o.value).includes(value))
                return t(FIELD_REQUIRED);
            },
          }}
          sx={{
            ...sx,
            ".MuiSelect-select": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      );
    }

    return (
      <Select
        ref={ref}
        label={t(label)}
        disabled={isDisabled}
        name={name}
        variant={variant}
        options={options}
        fullWidth={fullWidth}
        doTranslate={true}
        control={control}
        errors={errors}
        rules={{
          ...rules,
          validate: (value) => {
            if (!options.map((o) => o.value).includes(value))
              return t(FIELD_REQUIRED);
          },
        }}
        sx={sx}
      />
    );
  }
);

export default SelectCallToActionTikTok;
